import * as models from "@common/models/v2";
import type * as schemas from "@common/schemas/v2";
import { BaseStep } from "./base";
import { StepActions } from "./step";
import { makeObservable, observable, override } from "mobx";
import { FlowFlexibility } from "@common/schemas";
import { findElement } from "../../../element";

export class ForkStep extends BaseStep {
  attributes: models.ForkStepAttributes;
  actions: StepActions;

  targetElements: Map<string, Element | null> = new Map();
  clickHandlers: Map<string, (e: Event) => void> = new Map();
  clickedTargetId?: string;
  isClicked: boolean = false;
  stopTrackingTargetElements?: () => void;

  constructor(step: schemas.Step, actions: StepActions, flowFlexibility: FlowFlexibility) {
    super(step, actions, flowFlexibility);

    this.attributes = new models.ForkStepAttributes(step.attributes as schemas.ForkStepAttributes);
    this.actions = actions;
    makeObservable(this, {
      isClicked: observable,
      clickedTargetId: observable,
      targetElements: observable,
      isBlocking: override,
    });
  }

  async run() {
    super.run();
    this.isClicked = false;
    this.clickedTargetId = undefined;
    this.trackTargetElements();
  }

  private trackTargetElements() {
    // Clean up any existing tracking
    this.stopTrackingTargetElements?.();
    this.stopTrackingTargetElements = undefined;

    // Track using the step's own ID (like other steps do)
    this.stopTrackingTargetElements = this.actions.onTargetElementChange(this.id, (element) => {
      this.handleAllElementsChange();
    });
  }

  private handleAllElementsChange() {
    // Clear existing click listeners
    this.targetElements.forEach((element, targetId) => {
      const handler = this.clickHandlers.get(targetId);
      if (element && handler) {
        element.removeEventListener(this.attributes.clickDetectionMethod, handler, true);
      }
    });
    this.targetElements.clear();
    this.clickHandlers.clear();

    // Find all target elements manually
    this.attributes.targetElements.forEach((targetConfig) => {
      const element = findElement(targetConfig.targetElementSelector);

      if (element) {
        this.targetElements.set(targetConfig.id, element);

        // Create and store click handler for this target
        const clickHandler = (e: Event) => this.handleInteractionClick(e, targetConfig.id);
        this.clickHandlers.set(targetConfig.id, clickHandler);
        element.addEventListener(this.attributes.clickDetectionMethod, clickHandler, true);
      } else {
        this.targetElements.set(targetConfig.id, null);
      }
    });

    // Set the first available element as current target (for compatibility with existing systems)
    const firstAvailableElement = Array.from(this.targetElements.values()).find((el) => el !== null);
    this.actions.setCurrentTargetElement(firstAvailableElement || null);
  }

  private handleInteractionClick = (e: Event, targetId: string) => {
    if (this.isClicked) return; // Prevent multiple clicks

    this.isClicked = true;
    this.clickedTargetId = targetId;

    // Find the target element configuration and execute its action
    const targetConfig = this.attributes.targetElements.find((te) => te.id === targetId);
    if (targetConfig && targetConfig.action.type === "switchTour") {
      // Use the existing switchTour functionality
      this.actions.switchTour(targetConfig.action.guideId);
    }
  };

  dispose() {
    super.dispose();

    // Clean up target element tracking
    this.stopTrackingTargetElements?.();
    this.stopTrackingTargetElements = undefined;

    // Remove click listeners from all target elements
    this.targetElements.forEach((element, targetId) => {
      const handler = this.clickHandlers.get(targetId);
      if (element && handler) {
        element.removeEventListener(this.attributes.clickDetectionMethod, handler, true);
      }
    });

    this.targetElements.clear();
    this.clickHandlers.clear();
    this.actions.setCurrentTargetElement(undefined);
  }

  get additionalContent() {
    return this.attributes?.additionalContent;
  }

  get isTargetableStepType() {
    return true;
  }

  get isBlockingStepType() {
    return true;
  }

  get isBlocking() {
    return !this.isClicked;
  }

  // Expose all available target elements for multi-element highlighting
  get allTargetElements(): Element[] {
    return Array.from(this.targetElements.values()).filter((element): element is Element => element !== null);
  }
}
